/**
 * API Client with JWT Token Management and Automatic Refresh
 *
 * Features:
 * - Automatic JWT token attachment to requests
 * - Token refresh on 401 responses
 * - localStorage persistence for tokens
 * - Fallback to session-based auth
 * - Centralized error handling
 */

import axios from 'axios';
import { API_URL, LONG_RUNNING_API_URL } from '../config/api';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management utilities
export const tokenManager = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),

  setTokens: (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  hasTokens: () => {
    return !!(tokenManager.getAccessToken() && tokenManager.getRefreshToken());
  },
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - attach JWT token to outgoing requests
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = tokenManager.getAccessToken();

    // Attach JWT token if available
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // For FormData, let browser set Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors and refresh tokens
apiClient.interceptors.response.use(
  (response) => {
    // Success response, return as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if we have tokens to refresh
      if (!tokenManager.hasTokens()) {
        // No tokens available, reject with original error
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another request is already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();

      return new Promise((resolve, reject) => {
        // Attempt to refresh the token
        axios.post(`${API_URL}/refresh`, {}, {
          headers: {
            'Authorization': `Bearer ${refreshToken}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        })
          .then(({ data }) => {
            const newAccessToken = data.access_token;

            // Store new access token
            tokenManager.setTokens(newAccessToken, refreshToken);

            // Update authorization header
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            // Process queued requests
            processQueue(null, newAccessToken);

            // Retry original request
            resolve(apiClient(originalRequest));
          })
          .catch((err) => {
            // Refresh failed, clear tokens and redirect to login
            processQueue(err, null);
            tokenManager.clearTokens();

            // Dispatch custom event for app to handle (e.g., redirect to login)
            window.dispatchEvent(new CustomEvent('auth:token-expired'));

            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // For other errors, reject with original error
    return Promise.reject(error);
  }
);

// Convenience methods for common HTTP operations
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};

// Long-running API client (bypasses CloudFront 60s timeout by going direct to ALB)
// Use this for operations that may take >60s: OpenAI requests, connector test/sync, etc.
const longRunningApiClient = axios.create({
  baseURL: LONG_RUNNING_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes (gunicorn backend timeout is 300s)
});

// Apply same interceptors as regular API client
longRunningApiClient.interceptors.request.use(
  (config) => {
    const accessToken = tokenManager.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

longRunningApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && tokenManager.hasTokens()) {
      originalRequest._retry = true;
      const refreshToken = tokenManager.getRefreshToken();
      try {
        const { data } = await axios.post(`${API_URL}/refresh`, {}, {
          headers: { 'Authorization': `Bearer ${refreshToken}` },
          withCredentials: true,
        });
        tokenManager.setTokens(data.access_token, refreshToken);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return longRunningApiClient(originalRequest);
      } catch (err) {
        tokenManager.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

// Long-running API convenience methods
export const longApi = {
  get: (url, config) => longRunningApiClient.get(url, config),
  post: (url, data, config) => longRunningApiClient.post(url, data, config),
  put: (url, data, config) => longRunningApiClient.put(url, data, config),
  patch: (url, data, config) => longRunningApiClient.patch(url, data, config),
  delete: (url, config) => longRunningApiClient.delete(url, config),
};

export default apiClient;
